import {
  buildSplitPageGroups,
  convertImagesToPdf,
  extractPdfTextPages,
  getPdfPageCount,
  mergePdfDocuments,
  renderPdfToImages,
  serializePdfText,
  splitOutputName,
  splitPdfDocument,
  zipRenderedPages,
  type SplitMode
} from "@xhalo/document-browser-engine";
import type { ConversionTarget, DocumentOperation } from "@xhalo/document-contracts";

export interface LocalPdfParameters {
  ranges: string;
  format: "png" | "jpeg";
  dpi: number;
  quality: number;
  splitMode: SplitMode;
  pagesPerOutput: number;
  conversionTarget: ConversionTarget;
}

export interface LocalPdfRequest {
  id: string;
  operation: DocumentOperation;
  inputs: ArrayBuffer[];
  inputMimeTypes: string[];
  parameters: LocalPdfParameters;
}

export type LocalPdfResponse =
  | { id: string; ok: true; name: string; mime: string; buffer: ArrayBuffer }
  | { id: string; ok: false; error: string };

export interface LocalPdfResult { name: string; blob: Blob }
export interface LocalPdfExecutionOutput { name: string; mime: string; buffer: ArrayBuffer }

export async function processLocalPdf(
  operation: DocumentOperation,
  inputs: readonly Uint8Array[],
  parameters: LocalPdfParameters,
  inputMimeTypes: readonly string[] = inputs.map(() => "application/pdf")
): Promise<LocalPdfResult> {
  if (typeof Worker === "undefined") {
    const output = await executeLocalPdf(operation, inputs, parameters, inputMimeTypes);
    return { name: output.name, blob: new Blob([output.buffer], { type: output.mime }) };
  }
  const request: LocalPdfRequest = {
    id: crypto.randomUUID(),
    operation,
    inputs: inputs.map((input) => input.slice().buffer),
    inputMimeTypes: [...inputMimeTypes],
    parameters
  };
  const worker = new Worker(new URL("./pdf-worker.ts", import.meta.url), { type: "module", name: "xhalo-pdf-engine" });
  return new Promise<LocalPdfResult>((resolve, reject) => {
    worker.onmessage = (event: MessageEvent<LocalPdfResponse>) => {
      worker.terminate();
      if (!event.data.ok) return reject(new Error(event.data.error));
      resolve({ name: event.data.name, blob: new Blob([event.data.buffer], { type: event.data.mime }) });
    };
    worker.onerror = (event) => {
      worker.terminate();
      reject(new Error(event.message || "Local PDF worker failed."));
    };
    worker.postMessage(request, request.inputs);
  });
}

export async function executeLocalPdf(
  operation: DocumentOperation,
  inputs: readonly Uint8Array[],
  parameters: LocalPdfParameters,
  inputMimeTypes: readonly string[]
): Promise<LocalPdfExecutionOutput> {
  if (operation === "merge") {
    const output = await mergePdfDocuments(inputs);
    return { name: "xhalo-merged.pdf", mime: "application/pdf", buffer: output.slice().buffer };
  }
  if (operation === "split") {
    const pageCount = await getPdfPageCount(inputs[0]!);
    const groups = buildSplitPageGroups(parameters.splitMode, parameters.ranges, parameters.pagesPerOutput, pageCount);
    const outputs = await splitPdfDocument(inputs[0]!, groups);
    const entries = outputs.map((output, index) => ({ name: splitOutputName(groups[index]!, index), blob: new Blob([output.slice().buffer], { type: "application/pdf" }) }));
    const blob = await zipRenderedPages(entries);
    return { name: "xhalo-split.zip", mime: "application/zip", buffer: await blob.arrayBuffer() };
  }
  if (operation === "pdf_to_images") {
    const pages = await renderPdfToImages(inputs[0]!, {
      format: parameters.format,
      dpi: parameters.dpi,
      ...(parameters.ranges ? { pages: parameters.ranges } : {}),
      ...(parameters.format === "jpeg" ? { quality: parameters.quality } : {})
    });
    const blob = pages.length === 1 ? pages[0]!.blob : await zipRenderedPages(pages);
    return { name: pages.length === 1 ? pages[0]!.name : "xhalo-images.zip", mime: pages.length === 1 ? pages[0]!.blob.type : "application/zip", buffer: await blob.arrayBuffer() };
  }
  if (operation === "convert") {
    if (parameters.conversionTarget === "pdf") {
      const output = await convertImagesToPdf(inputs.map((bytes, index) => ({ bytes, mimeType: inputMimeTypes[index] ?? "" })));
      return { name: "xhalo-images.pdf", mime: "application/pdf", buffer: output.slice().buffer };
    }
    if (!["text", "markdown", "html", "json", "xml", "csv"].includes(parameters.conversionTarget)) {
      throw new Error("This conversion target requires cloud processing.");
    }
    if (inputs.length !== 1 || inputMimeTypes[0] !== "application/pdf") throw new Error("PDF text export requires one PDF input.");
    const output = serializePdfText(
      await extractPdfTextPages(inputs[0]!),
      parameters.conversionTarget as "text" | "markdown" | "html" | "json" | "xml" | "csv",
    );
    return { name: `xhalo-document.${output.extension}`, mime: output.mimeType, buffer: output.bytes.slice().buffer };
  }
  throw new Error("This operation requires cloud processing.");
}
